import type * as React from 'react';

import { usePanelTransitionStyle } from '@gitbook/spine-react';

import { AgentDockSlot } from '../Components';

/**
 * The column the layout keeps on the right of the content.
 *
 * It holds two things, and the split between them is the whole point of it: the page's side panel
 * at the top — rendered here rather than over the page, through the host below — and the anchored
 * agent under it. The agent has the column to itself until a page opens a panel, and takes it back
 * when that panel closes, so moving between pages never costs the conversation its place.
 *
 * Which of the two grows is decided in CSS, from whether the host is holding anything: nothing here
 * watches the page's side panels, and nothing has to be told when one opens.
 */
export function AppLayoutRail(props: {
    /**
     * Receives the element the page's side panel is rendered into. Handed to
     * `SidePanelHostContext` by the layout, which owns it: the panel has to be pointed at the host
     * during the same render the rail appears in, or it would mount over the page and move here a
     * frame later — remounting itself on the way.
     */
    hostRef: React.Ref<HTMLDivElement>;
}) {
    const { hostRef } = props;
    // The width follows the side panel's own transition, so expanding a panel widens the column at
    // the pace the panel itself changes size.
    const transitionStyle = usePanelTransitionStyle();

    return (
        <div
            data-testid="appLayout.rail"
            // The gutter is added to the width rather than taken out of it, so the cards inside
            // are exactly the width a side panel has over the page — a panel moving in here must
            // not change size on the way.
            className="flex w-[calc(var(--app-rail-width)+var(--app-layout-spacing-unit))] shrink-0 flex-col gap-1.5 py-1.5 pr-1.5 has-[[data-side-panel-expanded]]:w-[calc(var(--app-rail-width-expanded)+var(--app-layout-spacing-unit))]"
            style={{ ...transitionStyle, transitionProperty: 'width' }}
        >
            {/* Where the page's side panel lands. Empty for as long as the page has none open,
                which is also how the agent below comes to have the column to itself. */}
            <div
                ref={hostRef}
                data-app-rail-host
                className="flex min-h-64 flex-1 flex-col overflow-hidden empty:hidden"
            />
            {/* Both grow the same, so the column is halved when they share it and whole when only
                one of them is there. Neither goes under the floor, which on a short window takes
                the difference out of both rather than crushing one of them. */}
            <AgentDockSlot kind="rail" className="flex min-h-64 flex-1 flex-col" />
        </div>
    );
}
